// The Categories tab: how a project's files are sorted and organised. The
// tenant's categories with the files in each (`document-ai/0032`), the brief,
// the families with every placement and why, the change log, and every import
// with the steps that wrote them (`document-ai/0016`, `document-ai/0017`; the
// "families and placements" and "outline and brief" views of
// `document-ai/0022` §3).
//
// It was the Outline tab until 2026-10-03, when files moved from each
// project's own areas to the tenant's fixed categories (`document-ai/0032`).
// The outline went the same day; the brief has its own route.
//
// One write, and only one: Undo on a placement, behind a confirmation. Its
// route is listed in `WRITE_ROUTES`, and the entry it writes has no user,
// which is what "the backoffice did this" looks like (`architecture/0027`).
// Everything else here is read-only. Moving a file to another category is the
// dashboard's job.
import { useState, type ReactNode } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import type {
  AdminFamily,
  AdminFamilyMember,
  AdminLinksResponse,
  AdminProjectChange,
} from "@gb/admin-contracts";
import {
  FAMILY_ROLE_LABELS,
  IMPORT_STATUS_LABELS,
  LINK_FOUND_BY_LABELS,
  LINK_KIND_LABELS,
  LINK_KIND_PHRASES,
  LINK_STATE_LABELS,
  MADE_BY_LABELS,
  PARTY_ROLE_LABELS,
  PLACEMENT_RELATION_LABELS,
  PROJECT_CHANGE_KIND_LABELS,
  UNDOABLE_CHANGE_KINDS,
  type KnowledgeSummary,
  type ProjectBriefResponse,
  type ProjectChangeKind,
  type ProjectImport,
} from "@gb/contracts";
import { DataTable } from "@/components/gb/data-table";
import { SectionLabel } from "@/components/gb/section-label";
import { Button } from "@/components/ui/button";
import { useBrainSummary } from "../brain/brain-api";
import { ProcessingStepsPanel } from "../documents/processing-steps-panel";
import { useBrief, useChanges, useFamilies, useImports, useImportSteps, useLinks } from "./projects-api";
import { UndoChangeDialog, type UndoTarget } from "./undo-change-dialog";

type TabProps = { tenantId: string; projectId: string };

export function CategoriesTab({ tenantId, projectId }: TabProps) {
  // The categories are the Brain's shelves, so they come from the admin Brain
  // summary, the same numbers the Documents tab's line reads.
  const summary = useBrainSummary(tenantId, projectId);
  const brief = useBrief(tenantId, projectId);
  const imports = useImports(tenantId, projectId);
  const families = useFamilies(tenantId, projectId);
  const changes = useChanges(tenantId, projectId);
  const links = useLinks(tenantId, projectId);

  return (
    <div className="space-y-8">
      <section>
        <SectionLabel className="mb-2">Categories</SectionLabel>
        {summary.isError ? (
          <LoadError what="The categories" error={summary.error} />
        ) : (
          summary.data && <CategoriesView summary={summary.data} />
        )}
      </section>

      <section>
        <SectionLabel className="mb-2">Brief</SectionLabel>
        {brief.isError ? (
          <LoadError what="The brief" error={brief.error} />
        ) : (
          brief.data && <BriefView written={brief.data} />
        )}
      </section>

      <section>
        <SectionLabel
          className="mb-2"
          trailing={families.data ? `${families.data.families.length}` : undefined}
        >
          Families
        </SectionLabel>
        {families.isError ? (
          <LoadError what="The families" error={families.error} />
        ) : (
          families.data && (
            <FamiliesView families={families.data.families} questions={families.data.questions} />
          )
        )}
      </section>

      <section>
        <SectionLabel className="mb-2" trailing={links.data ? `${links.data.links.length}` : undefined}>
          Links
        </SectionLabel>
        {links.isError ? (
          <LoadError what="The links" error={links.error} />
        ) : (
          links.data && <LinksView data={links.data} />
        )}
      </section>

      <section>
        <SectionLabel
          className="mb-2"
          trailing={changes.data ? `${changes.data.changes.length}` : undefined}
        >
          Changes
        </SectionLabel>
        {changes.isError ? (
          <LoadError what="The changes" error={changes.error} />
        ) : (
          changes.data && (
            <ChangesView
              tenantId={tenantId}
              projectId={projectId}
              changes={changes.data.changes}
              undoRate={changes.data.undoRate}
            />
          )
        )}
      </section>

      <section>
        <SectionLabel
          className="mb-2"
          trailing={imports.data ? `${imports.data.length}` : undefined}
        >
          Imports
        </SectionLabel>
        {imports.isError ? (
          <LoadError what="The imports" error={imports.error} />
        ) : imports.data?.length === 0 ? (
          <p className="text-[0.82rem] text-muted">No imports yet.</p>
        ) : (
          <div className="space-y-2">
            {(imports.data ?? []).map((item) => (
              <ImportRow key={item.id} tenantId={tenantId} projectId={projectId} item={item} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

/**
 * Every link, in every state, and the removal rate per kind and way of
 * finding it (`document-ai/0024` §5): a high rate is the signal that a method
 * is wrong. A link closed because its file was deleted is not counted as
 * removed; that says nothing about the method.
 */
function LinksView({ data }: { data: AdminLinksResponse }) {
  if (data.links.length === 0) {
    return <p className="text-[0.82rem] text-muted">No links yet. Contains-links are read from the containers and are not listed here.</p>;
  }
  return (
    <div className="space-y-3">
      <DataTable
        columns={[
          { key: "kind", header: "Kind", render: (row) => LINK_KIND_LABELS[row.kind] },
          { key: "found", header: "Found by", render: (row) => LINK_FOUND_BY_LABELS[row.foundBy] },
          { key: "made", header: "Made", width: 80, render: (row) => row.made },
          {
            key: "removed",
            header: "Removed by a person",
            width: 170,
            render: (row) => `${row.removed} (${row.made === 0 ? 0 : Math.round((row.removed / row.made) * 100)}%)`,
          },
        ]}
        rows={data.removals}
        getRowKey={(row) => `${row.kind}-${row.foundBy}`}
        emptyMessage="No links."
      />
      <ul className="divide-y divide-border rounded-card border-[1.5px] border-border bg-surface">
        {data.links.map((link) => (
          <li key={link.id ?? `${link.from.id}-${link.to.id}`} className="px-3 py-2 text-[0.8rem]">
            <div className={link.state === "removed" ? "text-muted line-through" : "text-ink"}>
              “{link.from.name}” {LINK_KIND_PHRASES[link.kind].out} “{link.to.name}”
              {link.current ? ` (now “${link.current.name}”)` : ""}
            </div>
            <div className="text-[0.74rem] text-muted">
              {LINK_STATE_LABELS[link.state]} · {LINK_FOUND_BY_LABELS[link.foundBy]} · {MADE_BY_LABELS[link.madeBy]}
              {link.removed
                ? ` · removed ${new Date(link.removed.at).toLocaleString()} · ${MADE_BY_LABELS[link.removed.madeBy]}${link.removed.reason ? ` · ${link.removed.reason}` : ""}`
                : ""}
            </div>
            {link.reason && <div className="text-[0.74rem] text-ink-prose">{link.reason}</div>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function LoadError({ what, error }: { what: string; error: Error }) {
  return (
    <p className="text-[0.82rem] text-danger">
      {what} could not be loaded. {error.message}
    </p>
  );
}

function BriefView({ written }: { written: ProjectBriefResponse }) {
  const { brief } = written;
  if (!brief && written.writtenAt) {
    // A brief written before parties had roles and evidence (2026-09-25,
    // `document-ai/0017` §13). The API reads it as none rather than show its
    // bare names as if they were checked, and the next import rewrites it.
    return (
      <p className="text-[0.82rem] text-muted">
        The stored brief was written before parties had roles, so it is not shown. The next import
        rewrites it.
      </p>
    );
  }
  if (!brief) {
    return (
      <p className="text-[0.82rem] text-muted">
        No brief yet. It is written when an import is organised.
      </p>
    );
  }
  return (
    <div className="rounded-lg border border-border bg-white p-4">
      <p className="text-[0.85rem] leading-relaxed text-ink-prose">{brief.summary}</p>
      <dl className="mt-3 grid gap-x-6 gap-y-3 md:grid-cols-2">
        <BriefFact label="What is built">{brief.built}</BriefFact>
        <BriefFact label="Client">{brief.client}</BriefFact>
        <BriefFact label="The company's role">{brief.role}</BriefFact>
        <BriefFact label="Parties">
          <BriefList
            items={brief.parties.map((party) => ({
              name: party.name,
              what: PARTY_ROLE_LABELS[party.role],
              files: party.documentIds.length,
            }))}
          />
        </BriefFact>
        <BriefFact label="Suppliers">
          <BriefList
            items={brief.suppliers.map((supplier) => ({
              name: supplier.name,
              what: supplier.supplies,
              files: supplier.documentIds.length,
            }))}
          />
        </BriefFact>
      </dl>
      <div className="mt-3 font-mono text-[0.7rem] text-faint">
        {[
          written.madeBy ? MADE_BY_LABELS[written.madeBy] : null,
          written.writtenAt ? `written ${new Date(written.writtenAt).toLocaleString()}` : null,
        ]
          .filter(Boolean)
          .join(" · ")}
      </div>
    </div>
  );
}

function BriefFact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-[0.68rem] font-semibold tracking-wide text-muted uppercase">{label}</dt>
      <dd className="mt-0.5 text-[0.82rem] text-ink">{children ?? "—"}</dd>
    </div>
  );
}

/**
 * One line each: a name can carry its own comma. With what it is on this
 * project — a party's role, a supplier's supply — and how many files about the
 * project show it (2026-09-25, `document-ai/0017` §13).
 */
function BriefList({ items }: { items: { name: string; what: string; files: number }[] }) {
  if (items.length === 0) return <>—</>;
  return (
    <ul>
      {items.map((item, index) => (
        <li key={`${index}:${item.name}`}>
          {item.name} <span className="text-muted">· {item.what}</span>
          <span className="font-mono text-[0.7rem] text-faint">
            {" "}
            · shown by {item.files === 1 ? "1 file" : `${item.files} files`}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Every one of the tenant's categories, in the tenant's order, empty ones
 * included (`document-ai/0032`). Names and descriptions are the tenant's own,
 * as the API sends them, and may be in its language: nothing here holds a
 * second copy of the list.
 *
 * "Files" counts the files whose main category it is, so the column and "Not
 * sorted yet" add up to the files. "Also relevant" counts the files that live
 * in another category and list this one, and is not part of that sum. Both
 * are the admin summary's counts, which include hidden files.
 */
function CategoriesView({ summary }: { summary: KnowledgeSummary }) {
  return (
    <>
      <DataTable
        columns={[
          {
            key: "category",
            header: `Categories (${summary.categories.length})`,
            render: (c) => (
              <div className="min-w-0">
                <div className="truncate text-[0.85rem] font-semibold text-ink">{c.name}</div>
                <div className="text-[0.72rem] leading-snug text-faint">{c.description}</div>
              </div>
            ),
          },
          {
            // The key is what the card and the debug view store, so a support
            // question about a stored key can be matched to its name here.
            key: "key",
            header: "Key",
            width: 130,
            render: (c) => (
              <span className="font-mono text-[0.72rem] break-words text-muted">{c.key}</span>
            ),
          },
          {
            key: "files",
            header: "Files",
            width: 56,
            render: (c) => <span className="font-mono text-[0.75rem] text-ink">{c.documents}</span>,
          },
          {
            key: "alsoRelevant",
            header: "Also relevant",
            width: 110,
            render: (c) => (
              <span className="font-mono text-[0.75rem] text-muted">{c.alsoRelevant}</span>
            ),
          },
        ]}
        rows={summary.categories}
        getRowKey={(c) => c.key}
        emptyMessage="This company has no categories."
      />
      {/* The files with no main category: no card has picked one and no
          person has moved them, or their key has left the tenant's list. Not
          an organise gap: the card sorts a file, not an organise step. */}
      <div className="mt-2 font-mono text-[0.72rem] text-muted">
        Not sorted yet: {summary.notSorted}
      </div>
    </>
  );
}

/** The first eight characters of an ID: enough to tell entries apart on one page. */
function shortId(id: string): string {
  return id.slice(0, 8);
}

function FamiliesView({
  families,
  questions,
}: {
  families: AdminFamily[];
  questions: AdminFamilyMember[];
}) {
  return (
    <div className="space-y-6">
      {families.length === 0 ? (
        <p className="text-[0.82rem] text-muted">
          No families yet. Files become a family when placement finds they are versions of one
          document.
        </p>
      ) : (
        families.map((family) => <FamilyView key={family.id} family={family} />)
      )}

      <div>
        <SectionLabel className="mb-2" trailing={`${questions.length}`}>
          Waiting for a person
        </SectionLabel>
        <DataTable
          columns={[
            {
              key: "file",
              header: "File",
              render: (m) => <MemberName member={m} />,
            },
            {
              // Read as "This file is … X".
              key: "relation",
              header: "Proposed",
              width: 200,
              // The file's own part of its family's question: the first member
              // (VER-08, 2026-09-26).
              render: (m) => (
                <span className="text-[0.75rem] text-ink">
                  {m.question ? PLACEMENT_RELATION_LABELS[m.question.members[0]!.relation] : "—"}
                </span>
              ),
            },
            {
              key: "other",
              header: "Other file",
              width: 200,
              render: (m) => (
                <span className="text-[0.75rem] text-ink">{m.question?.members[0]!.other.name ?? "—"}</span>
              ),
            },
            {
              key: "reason",
              header: "Reason",
              render: (m) => (
                <span className="text-[0.72rem] leading-snug text-ink-prose">
                  {m.question?.members[0]!.reason ?? "—"}
                  {m.question && m.question.members.length > 1 && (
                    <span className="block text-[0.68rem] text-muted">
                      One question with {m.question.members.length - 1} other{" "}
                      {m.question.members.length === 2 ? "file" : "files"}
                    </span>
                  )}
                </span>
              ),
            },
          ]}
          rows={questions}
          getRowKey={(m) => m.id}
          emptyMessage="No questions waiting."
        />
      </div>
    </div>
  );
}

function MemberName({ member }: { member: AdminFamilyMember }) {
  return (
    <div className="min-w-0">
      <div className="truncate text-[0.82rem] font-semibold text-ink">{member.name}</div>
      {member.filename !== member.name && (
        <div className="truncate font-mono text-[0.7rem] text-faint">{member.filename}</div>
      )}
    </div>
  );
}

/** One family: who made it, and each member with what placement said about it. */
function FamilyView({ family }: { family: AdminFamily }) {
  // A relation never leaves its family (`document-ai/0016` §3, the checks),
  // so every related file is one of these.
  const names = new Map(family.members.map((m) => [m.id, m.name]));
  return (
    <div>
      <div className="mb-1.5 flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
        <span className="text-[0.88rem] font-semibold text-ink">{family.name ?? "Unnamed"}</span>
        <span className="text-[0.75rem] text-muted">
          {family.series ? "A series" : "Versions of one document"}
        </span>
        <span className="ml-auto font-mono text-[0.7rem] text-faint">
          {MADE_BY_LABELS[family.madeBy]} · made {new Date(family.createdAt).toLocaleString()}
        </span>
      </div>
      <DataTable
        columns={[
          {
            key: "file",
            header: `Files (${family.members.length})`,
            render: (m) => <MemberName member={m} />,
          },
          {
            key: "role",
            header: "Role",
            width: 140,
            render: (m) => (
              <div className="min-w-0">
                <div className="text-[0.75rem] text-ink">{FAMILY_ROLE_LABELS[m.role]}</div>
                {m.question && (
                  <div className="text-[0.68rem] font-semibold text-warn">Question waiting</div>
                )}
              </div>
            ),
          },
          {
            key: "related",
            header: "Relates to",
            width: 160,
            render: (m) => (
              <span className="text-[0.75rem] text-muted">
                {m.relatedDocumentId ? (names.get(m.relatedDocumentId) ?? m.relatedDocumentId) : "—"}
              </span>
            ),
          },
          {
            key: "madeBy",
            header: "Made by",
            width: 120,
            render: (m) => (
              <span className="text-[0.75rem] text-muted">
                {m.madeBy ? MADE_BY_LABELS[m.madeBy] : "Not placed yet"}
              </span>
            ),
          },
          {
            key: "reason",
            header: "Reason",
            render: (m) => (
              <span className="text-[0.72rem] leading-snug text-ink-prose">{m.reason ?? "—"}</span>
            ),
          },
          {
            key: "placedAt",
            header: "Placed at",
            width: 140,
            render: (m) => (
              <span className="font-mono text-[0.7rem] text-faint">
                {m.placedAt ? new Date(m.placedAt).toLocaleString() : "—"}
              </span>
            ),
          },
          {
            key: "date",
            header: "Date",
            width: 88,
            render: (m) => (
              <span className="font-mono text-[0.72rem] text-muted">{m.documentDate ?? "—"}</span>
            ),
          },
          {
            key: "revision",
            header: "Revision",
            width: 70,
            render: (m) => (
              <span className="font-mono text-[0.72rem] text-muted">{m.revision ?? "—"}</span>
            ),
          },
        ]}
        rows={family.members}
        getRowKey={(m) => m.id}
      />
    </div>
  );
}

/**
 * "The model placed 12 files itself; people undid 2." The undo rate is the
 * signal that the confidence bar is wrong (`document-ai/0022` §3).
 */
function undoRateLine({ modelPlacements, undone }: { modelPlacements: number; undone: number }) {
  if (modelPlacements === 0) return "The model has not placed any files itself yet.";
  const files = modelPlacements === 1 ? "file" : "files";
  const percent = Math.round((undone / modelPlacements) * 100);
  return `The model placed ${modelPlacements} ${files} itself; people undid ${undone} (${percent}%).`;
}

/**
 * A change can be undone when its kind can, and nobody has undone it yet. An
 * undo is never undone itself; its kind is not in the list either, but the
 * `undoesId` check says so without relying on that.
 */
function canUndo(change: AdminProjectChange): boolean {
  return (
    (UNDOABLE_CHANGE_KINDS as readonly ProjectChangeKind[]).includes(change.kind) &&
    change.undoneById === null &&
    change.undoesId === null
  );
}

function ChangesView({
  tenantId,
  projectId,
  changes,
  undoRate,
}: TabProps & {
  changes: AdminProjectChange[];
  undoRate: { modelPlacements: number; undone: number };
}) {
  // The change being asked about, held until the admin confirms.
  const [pending, setPending] = useState<UndoTarget | null>(null);

  const ask = (change: AdminProjectChange): void =>
    setPending({
      changeId: change.id,
      summary: [
        PROJECT_CHANGE_KIND_LABELS[change.kind],
        change.documentName ? `“${change.documentName}”` : null,
      ]
        .filter(Boolean)
        .join(" · "),
    });

  return (
    <>
      <p className="mb-2 text-[0.82rem] text-ink-prose">{undoRateLine(undoRate)}</p>
      {changes.length === 0 ? (
        <p className="text-[0.82rem] text-muted">No changes yet.</p>
      ) : (
        <ul aria-label="Change log" className="space-y-2">
          {changes.map((change) => (
            <ChangeRow
              key={change.id}
              change={change}
              onUndo={canUndo(change) ? () => ask(change) : undefined}
            />
          ))}
        </ul>
      )}

      <UndoChangeDialog
        tenantId={tenantId}
        projectId={projectId}
        target={pending}
        onClose={() => setPending(null)}
      />
    </>
  );
}

/** Who made a change. A person with no user is the backoffice (`architecture/0027`). */
function madeByLine(change: AdminProjectChange): string {
  const label = MADE_BY_LABELS[change.madeBy];
  if (change.userId) return `${label} · user ${shortId(change.userId)}`;
  if (change.madeBy === "person") return `${label} · the backoffice`;
  return label;
}

/** One entry in the change log, with its before and after as they were written. */
function ChangeRow({ change, onUndo }: { change: AdminProjectChange; onUndo?: () => void }) {
  return (
    <li className="rounded-lg border border-border bg-white px-4 py-3">
      <div className="flex items-baseline gap-3">
        <span className="shrink-0 text-[0.85rem] font-semibold text-ink">
          {PROJECT_CHANGE_KIND_LABELS[change.kind]}
        </span>
        {change.documentName && (
          <span className="min-w-0 truncate text-[0.8rem] text-ink-prose">
            {change.documentName}
          </span>
        )}
        {change.undoneById && (
          <span className="shrink-0 text-[0.72rem] font-semibold text-warn">
            Undone by entry {shortId(change.undoneById)}
          </span>
        )}
        <span className="ml-auto shrink-0 font-mono text-[0.7rem] text-faint">
          {new Date(change.createdAt).toLocaleString()}
        </span>
        {onUndo && (
          <Button variant="ghost" size="sm" className="self-center" onClick={onUndo}>
            Undo
          </Button>
        )}
      </div>
      <div className="mt-0.5 font-mono text-[0.7rem] text-faint">
        {[
          `entry ${shortId(change.id)}`,
          change.kind,
          madeByLine(change),
          change.undoesId ? `undoes entry ${shortId(change.undoesId)}` : null,
        ]
          .filter(Boolean)
          .join(" · ")}
      </div>
      {(change.before !== null || change.after !== null) && (
        <details className="mt-2">
          <summary className="cursor-pointer text-[0.72rem] text-muted">Before and after</summary>
          <div className="mt-1 grid gap-2 md:grid-cols-2">
            <RawRecord label="Before" value={change.before} />
            <RawRecord label="After" value={change.after} />
          </div>
        </details>
      )}
    </li>
  );
}

/** Shown raw: the backoffice is where a wrong placement is investigated. */
function RawRecord({ label, value }: { label: string; value: Record<string, unknown> | null }) {
  return (
    <div className="min-w-0">
      <div className="text-[0.68rem] font-semibold tracking-wide text-muted uppercase">{label}</div>
      <pre className="mt-0.5 overflow-x-auto rounded bg-canvas p-2 font-mono text-[0.7rem] text-ink-prose">
        {value === null ? "null" : JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}

/** One import, its counts on the row, and its steps fetched only when opened. */
function ImportRow({ tenantId, projectId, item }: TabProps & { item: ProjectImport }) {
  const [open, setOpen] = useState(false);
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <div className="rounded-lg border border-border bg-white">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="flex w-full cursor-pointer items-baseline gap-3 px-4 pt-3 pb-1 text-left"
      >
        <Chevron size={14} className="shrink-0 self-center text-muted" />
        <span className="text-[0.85rem] font-semibold text-ink">
          {IMPORT_STATUS_LABELS[item.status]}
        </span>
        <span className="font-mono text-[0.7rem] text-faint">
          {item.status} · {item.source}
          {item.programme ? " · programme" : ""}
        </span>
        <span className="ml-auto font-mono text-[0.72rem] text-muted">
          {item.files} files
          {/* What came out of a zip or an email, apart from what was uploaded
              (2026-09-25): carded and read count both. */}
          {item.unpacked > 0 ? ` · ${item.unpacked} unpacked` : ""} · {item.carded} carded · {item.read} read ·{" "}
          {item.failed} failed
          {item.refused > 0 ? ` · ${item.refused} refused` : ""}
          {/* Picked Drive files the project already holds, not stored again:
              counted so the picked number still adds up. */}
          {item.unchanged > 0 ? ` · ${item.unchanged} unchanged` : ""}
        </span>
      </button>
      <div className="px-4 pb-3 pl-[42px] font-mono text-[0.7rem] text-faint">
        opened {new Date(item.openedAt).toLocaleString()} ·{" "}
        {item.closedAt ? `closed ${new Date(item.closedAt).toLocaleString()}` : "still open"}
      </div>
      {open && (
        <div className="space-y-3 border-t border-border-inner px-4 py-3">
          {item.failures.length > 0 && (
            <div>
              <div className="text-[0.72rem] font-semibold text-muted">Picked but never arrived</div>
              <ul className="mt-1 space-y-0.5">
                {item.failures.map((failure, index) => (
                  <li key={`${index}:${failure.name}`} className="text-[0.75rem] text-ink-prose">
                    <span className="font-mono">{failure.name}</span> — {failure.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <ImportSteps tenantId={tenantId} projectId={projectId} importId={item.id} />
        </div>
      )}
    </div>
  );
}

function ImportSteps({ tenantId, projectId, importId }: TabProps & { importId: string }) {
  const steps = useImportSteps(tenantId, projectId, importId);
  if (steps.isError) return <LoadError what="The steps" error={steps.error} />;
  return <ProcessingStepsPanel steps={steps.data} />;
}
